import React from 'react';
import { motion } from 'framer-motion';

/**
 * FilterChip - Individual filter chip component
 * Handles different chip types and states
 * 
 * @param {string} type - Type of filter (date, select, multi-select, etc.)
 * @param {any} value - Current filter value
 * @param {Function} onChange - Callback when chip is clicked
 * @param {string} label - Display label for the chip
 * @param {boolean} isActive - Whether filter has non-default value
 * @param {Function} onOpenSheet - Callback to open filter sheet
 * @param {Object} icon - Icon component
 * @param {string} flag - Country flag emoji (for country filters)
 * @param {number} count - Count for multi-select filters
 */
const FilterChip = ({
  type,
  value,
  onChange,
  label,
  isActive = false,
  onOpenSheet,
  icon: Icon,
  flag,
  count,
  disabled = false,
  className = ''
}) => {
  const handleClick = () => {
    if (disabled) return;
    
    // For complex filters, open sheet
    if (type === 'date' || type === 'select' || type === 'multi-select') {
      onOpenSheet && onOpenSheet();
    } else {
      // For simple toggles, handle directly
      onChange && onChange(!value);
    }
  };

  const getChipStyles = () => {
    if (disabled) {
      return 'bg-white/5 text-white/30 border-white/10 cursor-not-allowed';
    }
    if (isActive) {
      return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30 shadow-sm shadow-emerald-500/10';
    }
    return 'bg-white/10 text-white/70 border-white/20 hover:bg-white/20 hover:text-white';
  };

  const formatLabel = () => {
    // Handle special label formatting
    if (type === 'date' && value) {
      const date = new Date(value);
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (date.toDateString() === today.toDateString()) {
        return 'Today';
      } else if (date.toDateString() === tomorrow.toDateString()) {
        return 'Tomorrow';
      } else {
        // Format as "Sat 8/23"
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const day = days[date.getDay()];
        const month = date.getMonth() + 1;
        const dayNum = date.getDate();
        return `${day} ${month}/${dayNum}`;
      }
    }
    
    if (type === 'multi-select' && count > 1) {
      return `${label} +${count - 1}`;
    }
    
    return label;
  };

  return (
    <motion.button
      onClick={handleClick}
      disabled={disabled}
      className={`
        inline-flex items-center gap-2 px-3 py-1.5 rounded-full
        text-sm font-medium border transition-all duration-200
        ${getChipStyles()}
        ${disabled ? '' : 'active:scale-95'}
        ${className}
      `}
      whileTap={disabled ? {} : { scale: 0.95 }}
      aria-pressed={isActive}
      aria-disabled={disabled}
    >
      {/* Icon */}
      {Icon && <Icon size={14} className="flex-shrink-0" />}
      
      {/* Country flag */}
      {flag && <span className="text-base flex-shrink-0">{flag}</span>}
      
      {/* Label */}
      <span className="truncate max-w-[120px]">{formatLabel()}</span>
      
      {/* Dropdown indicator for select types */}
      {(type === 'select' || type === 'multi-select' || type === 'date') && !disabled && (
        <svg 
          className="w-3 h-3 flex-shrink-0 opacity-50" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M19 9l-7 7-7-7" 
          />
        </svg>
      )}
      
      {/* Active pulse indicator */}
      {isActive && !disabled && (
        <span className="flex h-2 w-2 flex-shrink-0">
          <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
      )}
    </motion.button>
  );
};

export default FilterChip;